import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  adminForcePassword,
  getAdminSiteMetrics,
  getAdminUserDetail,
  getAdminUsers,
  updateAdminUser,
} from "../../../../api/auth";
import { PORTAL_UNIVERSITIES } from "./AssignQuestionsPanel";

const COURSES = ["Medicine", "Dentistry"];
const ROLES = [
  { value: "", label: "All roles" },
  { value: "student", label: "Student" },
  { value: "reviewer", label: "Reviewer" },
  { value: "uploader", label: "Uploader" },
];

const formatDate = (value) => {
  if (!value) return "Never";
  return new Date(value).toLocaleString();
};

const roleLabel = (role) => {
  if (!role) return "Student";
  return role.charAt(0).toUpperCase() + role.slice(1);
};

const AdminPortal = () => {
  const [siteMetrics, setSiteMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [tempPasswordResult, setTempPasswordResult] = useState(null);
  const [customTempPassword, setCustomTempPassword] = useState("");

  const [filters, setFilters] = useState({
    university: "",
    course: "",
    role: "",
    search: "",
  });

  const [editForm, setEditForm] = useState({
    university_name: "",
    course_name: "",
    role: "student",
  });

  const loadSiteMetrics = useCallback(async () => {
    try {
      const data = await getAdminSiteMetrics();
      setSiteMetrics(data);
    } catch (error) {
      console.error("Failed to load site metrics:", error);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const params = {};
      if (filters.university) params.university = filters.university;
      if (filters.course) params.course = filters.course;
      if (filters.role) params.role = filters.role;
      if (filters.search.trim()) params.search = filters.search.trim();

      const data = await getAdminUsers(params);
      setUsers(data.users || []);
    } catch (error) {
      console.error("Failed to load users:", error);
    } finally {
      setLoadingUsers(false);
    }
  }, [filters]);

  const loadUserDetail = useCallback(async (userId) => {
    setLoadingDetail(true);
    try {
      const data = await getAdminUserDetail(userId);
      setUserDetail(data);
      setEditForm({
        university_name: data.user.university_name || "",
        course_name: data.user.course_name || "",
        role: data.user.role || "student",
      });
    } catch (error) {
      console.error("Failed to load user detail:", error);
      setUserDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    loadSiteMetrics();
  }, [loadSiteMetrics]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (selectedUserId) {
      loadUserDetail(selectedUserId);
    } else {
      setUserDetail(null);
    }
  }, [selectedUserId, loadUserDetail]);

  const handleSaveUser = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    try {
      await updateAdminUser(selectedUserId, editForm);
      await loadUsers();
      await loadUserDetail(selectedUserId);
      alert("User updated.");
    } catch (error) {
      alert("Failed to update user.");
    } finally {
      setSaving(false);
    }
  };

  const handleForcePassword = async () => {
    if (!selectedUserId) return;
    try {
      const result = await adminForcePassword(
        selectedUserId,
        customTempPassword.trim() || undefined
      );
      setTempPasswordResult(result);
    } catch (error) {
      alert("Failed to set temporary password.");
    }
  };

  const overview = siteMetrics?.overview;
  const engagement = siteMetrics?.engagement;

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Admin
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        User management, progress tracking, and platform growth metrics.
      </Typography>

      {overview && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: "Total users", value: overview.total_users },
            { label: "Verified", value: overview.verified_users },
            { label: "Subscribed", value: overview.subscribed_users },
            { label: "Signups (7d)", value: overview.signups_7d },
            { label: "Signups (30d)", value: overview.signups_30d },
            { label: "Active (7d)", value: overview.active_7d },
            { label: "Active (30d)", value: overview.active_30d },
            {
              label: "QB answers (platform)",
              value: engagement?.total_qb_answers,
            },
          ].map(({ label, value }) => (
            <Grid item xs={6} sm={4} md={3} key={label}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="h6">{value ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {siteMetrics?.byUniversity?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Users by university & course
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>University</TableCell>
                  <TableCell>Course</TableCell>
                  <TableCell align="right">Users</TableCell>
                  <TableCell align="right">Subscribed</TableCell>
                  <TableCell align="right">Active (30d)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {siteMetrics.byUniversity.map((row) => (
                  <TableRow
                    key={`${row.university_name}-${row.course_name}`}
                  >
                    <TableCell>{row.university_name}</TableCell>
                    <TableCell>{row.course_name}</TableCell>
                    <TableCell align="right">{row.user_count}</TableCell>
                    <TableCell align="right">{row.subscribed_count}</TableCell>
                    <TableCell align="right">{row.active_30d}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Typography variant="h6" gutterBottom>
        User directory
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>University</InputLabel>
            <Select
              value={filters.university}
              label="University"
              onChange={(e) =>
                setFilters((f) => ({ ...f, university: e.target.value }))
              }
            >
              <MenuItem value="">All</MenuItem>
              {PORTAL_UNIVERSITIES.map((uni) => (
                <MenuItem key={uni} value={uni}>
                  {uni}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Course</InputLabel>
            <Select
              value={filters.course}
              label="Course"
              onChange={(e) =>
                setFilters((f) => ({ ...f, course: e.target.value }))
              }
            >
              <MenuItem value="">All</MenuItem>
              {COURSES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Role</InputLabel>
            <Select
              value={filters.role}
              label="Role"
              onChange={(e) =>
                setFilters((f) => ({ ...f, role: e.target.value }))
              }
            >
              {ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Search name or email"
            value={filters.search}
            onChange={(e) =>
              setFilters((f) => ({ ...f, search: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            {users.length} user{users.length !== 1 ? "s" : ""} shown
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 520 }}
          >
            {loadingUsers ? (
              <Box p={3} display="flex" justifyContent="center">
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>University</TableCell>
                    <TableCell>QB</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No users match filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow
                        key={user.user_id}
                        hover
                        selected={selectedUserId === user.user_id}
                        onClick={() => setSelectedUserId(user.user_id)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {user.first_name} {user.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {user.university_name}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {user.course_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{user.questions_answered ?? 0}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2, minHeight: 520 }}>
            {!selectedUserId ? (
              <Typography color="text.secondary" sx={{ pt: 4, textAlign: "center" }}>
                Select a user to view metrics and manage their account.
              </Typography>
            ) : loadingDetail ? (
              <Box display="flex" justifyContent="center" pt={4}>
                <CircularProgress />
              </Box>
            ) : userDetail ? (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {userDetail.user.first_name} {userDetail.user.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {userDetail.user.email} · @{userDetail.user.user_name}
                </Typography>

                <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                  <Chip
                    size="small"
                    label={roleLabel(userDetail.user.role)}
                    color={userDetail.user.role ? "primary" : "default"}
                  />
                  <Chip
                    size="small"
                    label={
                      userDetail.user.is_verified ? "Verified" : "Unverified"
                    }
                    color={userDetail.user.is_verified ? "success" : "warning"}
                  />
                  <Chip
                    size="small"
                    label={
                      userDetail.user.subscription_id
                        ? "Subscribed"
                        : "No subscription"
                    }
                  />
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Registered
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(userDetail.user.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Last login
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(userDetail.user.last_login_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Flagged questions
                    </Typography>
                    <Typography variant="body2">
                      {userDetail.metrics.flaggedQuestions}
                    </Typography>
                  </Grid>
                </Grid>

                <Typography variant="subtitle2" gutterBottom>
                  Question bank progress
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {userDetail.metrics.questionBank.answered} /{" "}
                  {userDetail.metrics.questionBank.total} answered (
                  {userDetail.metrics.questionBank.percentComplete}%)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  {userDetail.metrics.questionBank.correct} correct ·{" "}
                  {userDetail.metrics.questionBank.incorrect} incorrect
                </Typography>

                <Typography variant="subtitle2" gutterBottom>
                  Mock bank progress
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {userDetail.metrics.mockBank.longFormatAnswered} /{" "}
                  {userDetail.metrics.mockBank.totalQuestions} long-format mock
                  questions submitted
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  {userDetail.metrics.mockBank.examsCompleted} mock exam
                  session(s) completed. Type-1 MCQ mock answers are not stored
                  per-user in the database yet.
                </Typography>

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Edit account
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>University</InputLabel>
                      <Select
                        value={editForm.university_name}
                        label="University"
                        onChange={(e) =>
                          setEditForm((f) => ({
                            ...f,
                            university_name: e.target.value,
                          }))
                        }
                      >
                        {PORTAL_UNIVERSITIES.map((uni) => (
                          <MenuItem key={uni} value={uni}>
                            {uni}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Course</InputLabel>
                      <Select
                        value={editForm.course_name}
                        label="Course"
                        onChange={(e) =>
                          setEditForm((f) => ({
                            ...f,
                            course_name: e.target.value,
                          }))
                        }
                      >
                        {COURSES.map((c) => (
                          <MenuItem key={c} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={editForm.role}
                        label="Role"
                        onChange={(e) =>
                          setEditForm((f) => ({ ...f, role: e.target.value }))
                        }
                      >
                        <MenuItem value="student">Student</MenuItem>
                        <MenuItem value="reviewer">Reviewer</MenuItem>
                        <MenuItem value="uploader">Uploader</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Box display="flex" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveUser}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save changes"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => {
                      setCustomTempPassword("");
                      setTempPasswordResult(null);
                      setPasswordDialogOpen(true);
                    }}
                  >
                    Set temporary password
                  </Button>
                </Box>
              </Box>
            ) : null}
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set temporary password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use this if you need emergency access for a user. Leave blank to
            auto-generate a password.
          </Typography>
          <TextField
            fullWidth
            label="Custom temporary password (optional)"
            value={customTempPassword}
            onChange={(e) => setCustomTempPassword(e.target.value)}
            type="text"
          />
          {tempPasswordResult && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Email:</strong> {tempPasswordResult.email}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Temporary password:</strong>{" "}
                {tempPasswordResult.temporary_password}
              </Typography>
              <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                Copy this now — it will not be shown again.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Close</Button>
          <Button variant="contained" color="warning" onClick={handleForcePassword}>
            Set password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPortal;
